import * as React from "react";
import {
  CalcBusterItem,
  Modernizations,
  ServiceItem,
} from "../../calcBusterTypes";
import { USDollar } from "../../util";
import { BarChart, BarChartProps, Box } from "@amzn/awsui-components-react";

const PathwaysBarChart: React.FC<{
  modernizations: Modernizations;
  services: ServiceItem[];
}> = ({ modernizations, services }) => {
  const chartData = React.useMemo(() => {
    if (!services || !modernizations || !modernizations.paths) {
      return [];
    }

    // Get modern services
    const modernServices = services.filter(
      (service) => service.isModernService
    );

    // Group services by modernization path
    const servicesByPath: Record<string, ServiceItem[]> = {};
    modernServices.forEach((service) => {
      if (service.modernizationPaths) {
        service.modernizationPaths.forEach((path) => {
          if (!servicesByPath[path]) {
            servicesByPath[path] = [];
          }
          servicesByPath[path].push(service);
        });
      }
    });
    // Create series for each service code
    const uniqueServiceCodes = [
      ...new Set(modernServices.map((service) => service.serviceCode)),
    ];

    return uniqueServiceCodes.map((serviceCode) => ({
      title: serviceCode,
      type: "bar" as const,
      valueFormatter: (value: number) => USDollar.format(value),
      data: modernizations.paths
        .map((path) => ({
          x: path.name,
          y:
            (servicesByPath[path.name]
              ?.filter((service) => service.serviceCode === serviceCode)
              .reduce((sum, service) => sum + service.serviceCost, 0) || 0) *
            12,
        }))
        .filter((item) => item.y > 0),
    }));
  }, [modernizations, services]);

  // Calculate total annual run rate
  const totalModernCost = React.useMemo(() => {
    if (!services) {
      return 0;
    }
    return (
      services
        .filter((service: ServiceItem) => service.isModernService)
        .reduce(
          (sum: number, service: ServiceItem) => sum + service.serviceCost,
          0
        ) * 12
    );
  }, [services]);

  // Get modernization index
  const modernizationIndex = modernizations?.index || 0;

  return (
    <Box>
      <BarChart
        series={chartData}
        xTitle="Pathway"
        yTitle="Cost (USD)"
        // hideFilter
        height={200}
        loadingText="Loading chart"
        statusType="finished"
        emphasizeBaselineAxis
        horizontalBars
        stackedBars
        empty={
          <Box textAlign="center" color="inherit">
            <b>No data available</b>
            <Box variant="p" color="inherit">
              There are no modernization services data available
            </Box>
          </Box>
        }
        ariaLabel="Modernization services by pathway"
        i18nStrings={{
          filterLabel: "Filter displayed data",
          filterPlaceholder: "Filter data",
          filterSelectedAriaLabel: "selected",
          legendAriaLabel: "Legend",
          chartAriaRoleDescription: "stacked bar chart",
          xAxisAriaRoleDescription: "x axis",
          yAxisAriaRoleDescription: "y axis",
        }}
        yTickFormatter={(value) => USDollar.format(value)}
        xScaleType="categorical"
        yScaleType="linear"
      />
      <Box textAlign="right" color="text-body-secondary" padding={{ top: "s" }}>
        <div>
          <strong>
            Total Modern Services Cost: {USDollar.format(totalModernCost)}
          </strong>
        </div>
        <div>Modernization Index: {modernizationIndex}%</div>
      </Box>
    </Box>
  );
};

export default PathwaysBarChart;
